// Asignar una tarifa a lo elegido en el plano (una ubicación, filas o mesas).
// Una tarifa se vende en una sola ubicación, así que:
// - si todavía no se vende en ningún lado, lo elegido se separa en una
//   ubicación nueva con esa tarifa;
// - si ya se vende en una ubicación de la misma familia (la original o una
//   parte separada de ella), lo elegido se suma a esa ubicación;
// - si se vende en otra, lo elegido pasa a ser una parte de esa ubicación, en
//   el mismo lugar del plano (mismas tarifas y mismo cupo).
import { ajustarCaja, aplicarFilas, configFilas, indiceEnBloque, separarFilas, separarMesas } from "./generador";
import { moverTarifa, sectorDeTarifa, motivoNoMover, conLugares, sePuedeBorrar } from "./mapa";
import { elementosDe } from "./seleccion";
import { esParte, hacerParte, llevar, nombreLibre, nombresUsados, separarParte, ubicacionDe } from "./partes";

const buscar = (mapa, clave) => mapa.sectores.find((s) => s.clave === clave);
const reemplazar = (mapa, sector) => ({ ...mapa, sectores: mapa.sectores.map((s) => (s.clave === sector.clave ? sector : s)) });

// raiz: la ubicación de la que salió (una parte cuenta como su ubicación)
const raiz = (mapa, s) => {
  const principal = esParte(s) ? buscar(mapa, s.editor.parteDe) || s : s;
  return principal.editor?.pieza || principal.clave;
};

// una parte separada que se quedó sin lugares se borra
const limpiar = (mapa, clave) => {
  const s = buscar(mapa, clave);
  return s && (s.editor?.pieza || s.editor?.parteDe) && !conLugares(s) ? { ...mapa, sectores: mapa.sectores.filter((x) => x.clave !== clave) } : mapa;
};

const sumarMesas = (mapa, desde, hacia, claves) => {
  const elegidas = desde.mesas.filter((m) => claves.includes(m.clave));
  if (elegidas.some((m) => m.vendida || m.sillas.some((x) => x.vendido))) return { mapa, error: "Hay mesas con algo vendido." };
  const giro = (desde.rotacion || 0) - (hacia.rotacion || 0);
  const usados = nombresUsados(mapa, ubicacionDe(hacia));
  const llevadas = elegidas.map(({ id, vendida, ...m }) => {
    const p = llevar(desde, hacia, m.x, m.y);
    const nombre = nombreLibre(usados, m.nombre);
    usados.add(nombre.toLowerCase());
    return { ...m, nombre, x: Math.round(p.x), y: Math.round(p.y), rotacion: (m.rotacion || 0) + giro, sillas: m.sillas.map(({ id: _id, vendido, ...x }) => x) };
  });
  const nuevoHacia = ajustarCaja({ ...hacia, mesas: [...hacia.mesas, ...llevadas] });
  const resto = { ...desde, mesas: desde.mesas.filter((m) => !claves.includes(m.clave)) };
  const nuevoDesde = conLugares(resto) ? ajustarCaja(resto) : resto;
  return { mapa: limpiar(reemplazar(reemplazar(mapa, nuevoDesde), nuevoHacia), desde.clave), error: null };
};

const sumarFilas = (mapa, desde, hacia, indices) => {
  if (indices.some((k) => desde.filas[k]?.asientos.some((a) => a.vendido))) return { mapa, error: "Hay asientos vendidos en esas filas." };
  const origenes = desde.editor?.filasDeOrigen;

  // vuelven a su bloque original: se dejan de excluir ahí
  if (hacia.editor?.filas && desde.editor?.pieza === hacia.clave && origenes) {
    const vuelven = indices.map((k) => origenes[k]);
    const c = configFilas(hacia);
    const r = aplicarFilas(hacia, { ...c, excluidas: (c.excluidas || []).filter((f) => !vuelven.includes(f)) });
    if (r.error) return { mapa, error: r.error };
    const resto = {
      ...desde,
      filas: desde.filas.filter((_, k) => !indices.includes(k)),
      editor: { ...desde.editor, filasDeOrigen: origenes.filter((_, k) => !indices.includes(k)) },
    };
    const nuevoDesde = conLugares(resto) ? ajustarCaja(resto) : resto;
    return { mapa: limpiar(reemplazar(reemplazar(mapa, nuevoDesde), r.sector), desde.clave), error: null };
  }

  // a una parte separada (filas sueltas): se copian en el mismo lugar
  if (!hacia.editor?.filas) {
    const usados = nombresUsados(mapa, ubicacionDe(hacia));
    const repetida = indices.find((k) => usados.has(desde.filas[k].nombre.toLowerCase()));
    if (repetida !== undefined) return { mapa, error: `Ya hay una fila ${desde.filas[repetida].nombre} en ${hacia.nombre}.` };
    const giro = (desde.rotacion || 0) - (hacia.rotacion || 0);
    const llevadas = indices.map((k) => {
      const f = desde.filas[k];
      const p = llevar(desde, hacia, f.x, f.y);
      return {
        ...f,
        x: Math.round(p.x),
        y: Math.round(p.y),
        asientos: f.asientos.map(({ id, vendido, ...a }) => {
          const q = llevar(desde, hacia, a.x, a.y);
          return { ...a, x: Math.round(q.x), y: Math.round(q.y), rotacion: Math.round((a.rotacion || 0) + giro) };
        }),
      };
    });
    // si vienen del bloque del que salió esta parte, se anota de dónde, para poder volver
    const delOrigen = hacia.editor?.pieza === desde.clave && desde.editor?.filas;
    const editorHacia = delOrigen
      ? { ...hacia.editor, filasDeOrigen: [...(hacia.editor.filasDeOrigen || []), ...indices.map((k) => indiceEnBloque(desde, k))] }
      : hacia.editor;
    const nuevoHacia = ajustarCaja({ ...hacia, filas: [...hacia.filas, ...llevadas], editor: editorHacia });

    let nuevoDesde;
    if (desde.editor?.filas) {
      const c = configFilas(desde);
      const r = aplicarFilas(desde, { ...c, excluidas: [...(c.excluidas || []), ...indices.map((k) => indiceEnBloque(desde, k))] });
      if (r.error) return { mapa, error: r.error };
      nuevoDesde = r.sector;
    } else {
      const resto = {
        ...desde,
        filas: desde.filas.filter((_, k) => !indices.includes(k)),
        editor: origenes ? { ...desde.editor, filasDeOrigen: origenes.filter((_, k) => !indices.includes(k)) } : desde.editor,
      };
      nuevoDesde = conLugares(resto) ? ajustarCaja(resto) : resto;
    }
    return { mapa: limpiar(reemplazar(reemplazar(mapa, nuevoDesde), nuevoHacia), desde.clave), error: null };
  }

  return { mapa, error: `Esas filas no se pueden sumar a ${hacia.nombre}: armalas desde el diseño.` };
};

// gruposDe: lo elegido agrupado por ubicación y tipo, [{ clave, indices }] o [{ clave, mesas }]
export const gruposDe = (seleccion) => {
  const grupos = new Map();
  elementosDe(seleccion).forEach((e) => {
    const k = `${e.tipo}|${e.sector}`;
    if (!grupos.has(k)) grupos.set(k, e.tipo === "fila" ? { clave: e.sector, indices: [] } : { clave: e.sector, mesas: [] });
    const g = grupos.get(k);
    if (e.tipo === "fila") g.indices.push(e.indice);
    else g.mesas.push(e.clave);
  });
  return [...grupos.values()];
};

// una parte separada elegida entera cuenta como todos sus lugares
const comoElementos = (sector) =>
  sector.filas.length > 0 ? { clave: sector.clave, indices: sector.filas.map((_, k) => k) } : { clave: sector.clave, mesas: sector.mesas.map((m) => m.clave) };

const completo = (sector, g) =>
  g.indices
    ? sector.mesas.length === 0 && new Set(g.indices).size === sector.filas.length
    : sector.filas.length === 0 && new Set(g.mesas).size === sector.mesas.length;

// soltarParte: la parte deja su ubicación y pasa a vender sólo esta tarifa
const soltarParte = (mapa, clave, idtarifa) => moverTarifa(separarParte(mapa, clave), idtarifa, clave);

// hacia una ubicación que no es de la familia: todo el sector o lo elegido
// (separado antes) pasa a ser una parte suya
const sumarComoParte = (mapa, desde, g, ubicacion) => {
  const principal = buscar(mapa, ubicacion);
  if (!conLugares(principal)) {
    return { mapa, error: `${principal.nombre} es general (se vende por cantidad): no se le pueden sumar mesas ni filas.` };
  }
  if (completo(desde, g)) {
    if (!esParte(desde) && desde.tarifas.length > 0) {
      return { mapa, error: `Es todo ${desde.nombre}, que vende otras entradas: quitáselas primero o elegí sólo una parte.` };
    }
    if (!sePuedeBorrar(desde)) return { mapa, error: `${desde.nombre} tiene entradas vendidas.` };
    return { mapa: hacerParte(mapa, desde.clave, ubicacion), clave: desde.clave, error: null };
  }
  const r = g.indices ? separarFilas(mapa, desde.clave, g.indices, null) : separarMesas(mapa, desde.clave, g.mesas, null);
  if (r.error) return r;
  return { mapa: hacerParte(r.mapa, r.clave, ubicacion), clave: r.clave, error: null };
};

// asignarTarifa aplica la asignación y devuelve { mapa, clave (lo que conviene
// elegir después), error }
export const asignarTarifa = (mapa, seleccion, idtarifa) => {
  const actual = buscar(mapa, sectorDeTarifa(mapa, idtarifa));
  let grupos;
  if (seleccion?.tipo === "sector") {
    const sector = buscar(mapa, seleccion.clave);
    if (!sector) return { mapa, error: "Elegí en el plano una ubicación, filas o mesas." };
    if (!actual) {
      const m = esParte(sector) ? soltarParte(mapa, sector.clave, idtarifa) : moverTarifa(mapa, idtarifa, sector.clave);
      return { mapa: m, clave: sector.clave, error: null };
    }
    if (!conLugares(sector) && ubicacionDe(sector) !== actual.clave) {
      return { mapa, error: "Una ubicación general no se puede sumar a otra." };
    }
    grupos = [comoElementos(sector)];
  } else {
    grupos = gruposDe(seleccion);
  }
  if (grupos.length === 0) return { mapa, error: "Elegí en el plano una ubicación, filas o mesas." };

  let m = mapa;
  let destino = actual?.clave;
  let elegir = actual?.clave;
  for (const g of grupos) {
    const desde = buscar(m, g.clave);
    if (!desde) continue;
    if (!destino) {
      const r =
        esParte(desde) && completo(desde, g)
          ? { mapa: soltarParte(m, desde.clave, idtarifa), clave: desde.clave }
          : g.indices
            ? separarFilas(m, g.clave, g.indices, idtarifa)
            : separarMesas(m, g.clave, g.mesas, idtarifa);
      if (r.error) return { mapa, error: r.error };
      m = r.mapa;
      destino = elegir = r.clave;
      continue;
    }
    if (ubicacionDe(desde) === destino) continue;
    const hacia = buscar(m, destino);
    const r =
      !esParte(desde) && raiz(m, desde) === raiz(m, hacia)
        ? g.indices
          ? sumarFilas(m, desde, hacia, g.indices)
          : sumarMesas(m, desde, hacia, g.mesas)
        : sumarComoParte(m, desde, g, destino);
    if (r.error) return { mapa, error: r.error };
    m = r.mapa;
    if (r.clave) elegir = r.clave;
  }
  return { mapa: m, clave: elegir, error: null };
};

// motivoNoAsignar: por qué la tarifa no se puede asignar a lo elegido (o null)
export const motivoNoAsignar = (mapa, tarifa, seleccion) => {
  const r = asignarTarifa(mapa, seleccion, tarifa.id);
  if (r.error) return r.error;
  if (r.mapa === mapa) return "Ya se vende ahí.";
  if (!sectorDeTarifa(mapa, tarifa.id)) {
    const sector = buscar(r.mapa, r.clave);
    const original = seleccion?.tipo === "sector" ? buscar(mapa, seleccion.clave) : null;
    return motivoNoMover(mapa, tarifa, original && !esParte(original) ? original : { ...sector, id: undefined });
  }
  return null;
};
